import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useApi from "../../hooks/useApi";

const SyncSupport = () => {
  const navigate = useNavigate();
  const { getData, postData } = useApi();
  const getDataRef = useRef(getData);
  const postDataRef = useRef(postData);
  const currentUserId = useSelector((state) => state.authentication.userId);

  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const [activeTab, setActiveTab] = useState(0);
  const [canAccessSupport, setCanAccessSupport] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    province: "",
    supervisorId: "",
    repId: "",
    errorStatus: "all",
    dateFrom: today,
    dateTo: today,
  });

  const [filterOptions, setFilterOptions] = useState({
    provinces: [],
    supervisors: [],
    reps: [],
  });

  const [syncSummaryRows, setSyncSummaryRows] = useState([]);
  const [errorStats, setErrorStats] = useState({
    totalQueue: 0,
    pendingRetry: 0,
    failed: 0,
    ignored: 0,
    resolved: 0,
  });
  const [errorQueueRows, setErrorQueueRows] = useState([]);
  const [selectedError, setSelectedError] = useState(null);
  const [errorsByEntity, setErrorsByEntity] = useState([]);
  const [errorsByType, setErrorsByType] = useState([]);

  useEffect(() => {
    getDataRef.current = getData;
  }, [getData]);

  useEffect(() => {
    postDataRef.current = postData;
  }, [postData]);

  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      if (!currentUserId) {
        if (isMounted) {
          setCanAccessSupport(false);
          setAccessChecked(true);
        }
        return;
      }

      const response = await getDataRef.current({
        entityName: "User",
        id: currentUserId,
      });

      if (!isMounted) return;

      setCanAccessSupport(
        response?.data?.permissions?.canAccessSupport === true,
      );
      setAccessChecked(true);
    };

    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!accessChecked || !canAccessSupport) return;

      setLoading(true);
      setError("");

      try {
        const [optionsResponse, syncSummaryResponse, errorQueueResponse] =
          await Promise.all([
            postDataRef.current({
              entityName: "SyncDashboard",
              action: "getSyncReportFilterOptions",
              data: { filters },
            }),
            postDataRef.current({
              entityName: "SyncDashboard",
              action: "getSyncReportSummaryByRep",
              data: { filters },
            }),
            postDataRef.current({
              entityName: "SyncDashboard",
              action: "getSyncErrorQueue",
              data: { filters, limit: 300 },
            }),
          ]);

        if (!isMounted) return;

        if (optionsResponse?.isSuccessful) {
          setFilterOptions({
            provinces: Array.isArray(optionsResponse?.data?.provinces)
              ? optionsResponse.data.provinces
              : [],
            supervisors: Array.isArray(optionsResponse?.data?.supervisors)
              ? optionsResponse.data.supervisors
              : [],
            reps: Array.isArray(optionsResponse?.data?.reps)
              ? optionsResponse.data.reps
              : [],
          });
        } else {
          setFilterOptions({ provinces: [], supervisors: [], reps: [] });
        }

        if (syncSummaryResponse?.isSuccessful) {
          setSyncSummaryRows(
            Array.isArray(syncSummaryResponse?.data)
              ? syncSummaryResponse.data
              : [],
          );
        } else {
          setSyncSummaryRows([]);
        }

        if (errorQueueResponse?.isSuccessful) {
          setErrorStats({
            totalQueue: Number(errorQueueResponse?.data?.totalQueue || 0),
            pendingRetry: Number(
              errorQueueResponse?.data?.statusCounts?.pending_retry || 0,
            ),
            failed: Number(errorQueueResponse?.data?.statusCounts?.failed || 0),
            ignored: Number(
              errorQueueResponse?.data?.statusCounts?.ignored || 0,
            ),
            resolved: Number(
              errorQueueResponse?.data?.statusCounts?.resolved || 0,
            ),
          });

          setErrorQueueRows(
            Array.isArray(errorQueueResponse?.data?.rows)
              ? errorQueueResponse.data.rows
              : [],
          );

          setSelectedError(null);

          setErrorsByEntity(
            Array.isArray(errorQueueResponse?.data?.byEntity)
              ? errorQueueResponse.data.byEntity
              : [],
          );

          setErrorsByType(
            Array.isArray(errorQueueResponse?.data?.byType)
              ? errorQueueResponse.data.byType
              : [],
          );
        } else {
          setErrorStats({
            totalQueue: 0,
            pendingRetry: 0,
            failed: 0,
            ignored: 0,
            resolved: 0,
          });
          setErrorQueueRows([]);
          setSelectedError(null);
          setErrorsByEntity([]);
          setErrorsByType([]);
        }

        if (
          !optionsResponse?.isSuccessful ||
          !syncSummaryResponse?.isSuccessful ||
          !errorQueueResponse?.isSuccessful
        ) {
          setError("Unable to load complete sync support data");
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Unable to load sync support data");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [accessChecked, canAccessSupport, filters]);

  const handleToolbarClick = (item) => {
    if (item === "synchronization") {
      navigate("/synchronization");
    }
  };

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const formatDateTime = (value, dateFormat = "yyyy-MM-dd HH:mm") => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return format(date, dateFormat);
  };

  const formatDuration = (durationMs) => {
    const value = Number(durationMs);
    if (!Number.isFinite(value) || value <= 0) return "-";

    const totalSeconds = Math.floor(value / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  };

  const availableFilterOptions = useMemo(
    () => ({
      provinces: Array.isArray(filterOptions?.provinces)
        ? filterOptions.provinces
        : [],
      supervisors: Array.isArray(filterOptions?.supervisors)
        ? filterOptions.supervisors
        : [],
      reps: Array.isArray(filterOptions?.reps) ? filterOptions.reps : [],
    }),
    [filterOptions],
  );

  if (!accessChecked) {
    return (
      <Page
        pageName="syncSupport"
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[{ name: "synchronization", iconName: "Sync" }]}
      >
        <Typography>Loading support access...</Typography>
      </Page>
    );
  }

  if (!canAccessSupport) {
    return (
      <Page
        pageName="syncSupport"
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[{ name: "synchronization", iconName: "Sync" }]}
      >
        <Alert severity="warning">
          You do not have access to Sync Support.
        </Alert>
      </Page>
    );
  }

  return (
    <Page
      pageName="syncSupport"
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[{ name: "synchronization", iconName: "Sync" }]}
    >
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Card elevation={2}>
          <CardContent>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={2}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Province</InputLabel>
                  <Select
                    value={filters.province}
                    label="Province"
                    onChange={handleFilterChange("province")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.provinces.map((province) => (
                      <MenuItem
                        key={province}
                        value={province}
                      >
                        {province}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    value={filters.supervisorId}
                    label="Supervisor"
                    onChange={handleFilterChange("supervisorId")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.supervisors.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Rep</InputLabel>
                  <Select
                    value={filters.repId}
                    label="Rep"
                    onChange={handleFilterChange("repId")}
                  >
                    <MenuItem value="">All</MenuItem>
                    {availableFilterOptions.reps.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
                sx={{ minWidth: "150px" }}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>Error Status</InputLabel>
                  <Select
                    value={filters.errorStatus}
                    label="Error Status"
                    onChange={handleFilterChange("errorStatus")}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending_retry">Pending Retry</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="ignored">Ignored</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  value={filters.dateFrom}
                  onChange={handleFilterChange("dateFrom")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={2}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  value={filters.dateTo}
                  onChange={handleFilterChange("dateTo")}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
        >
          <Tab label="Sync Summary" />
          <Tab label="Sync Error Summary" />
        </Tabs>

        {activeTab === 0 ? (
          <Card elevation={2}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
              >
                Sync Summary By Rep
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rep</TableCell>
                      <TableCell>Supervisor</TableCell>
                      <TableCell>Province</TableCell>
                      <TableCell align="right">Sync Records</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Errors/Timeouts</TableCell>
                      <TableCell align="right">Success Count</TableCell>
                      <TableCell align="right">Error Count</TableCell>
                      <TableCell>Last Successful Sync</TableCell>
                      <TableCell align="right">Avg Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncSummaryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Typography variant="body2">
                            No records found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      syncSummaryRows.map((row, index) => (
                        <TableRow key={`${row.repId || "rep"}-${index}`}>
                          <TableCell>{row.repName || "Unknown"}</TableCell>
                          <TableCell>
                            {row.supervisorName || "Unassigned"}
                          </TableCell>
                          <TableCell>{row.province || "-"}</TableCell>
                          <TableCell align="right">
                            {Number(row.totalSyncRecords || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.completedSyncRecords || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.errorSyncRecords || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.totalSuccessCount || 0)}
                          </TableCell>
                          <TableCell align="right">
                            {Number(row.totalErrorCount || 0)}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.lastSuccessfulSyncAt)}
                          </TableCell>
                          <TableCell align="right">
                            {formatDuration(row.avgDurationMs)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={3}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="overline">Queued Errors</Typography>
                    <Typography variant="h4">
                      {errorStats.totalQueue}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="overline">Pending Retry</Typography>
                    <Typography variant="h4">
                      {errorStats.pendingRetry}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="overline">Failed</Typography>
                    <Typography variant="h4">{errorStats.failed}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="overline">
                      Ignored / Resolved
                    </Typography>
                    <Typography variant="h4">
                      {errorStats.ignored} / {errorStats.resolved}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Sync Error Queue
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Created</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Rep</TableCell>
                        <TableCell>Supervisor</TableCell>
                        <TableCell>Province</TableCell>
                        <TableCell>Entity</TableCell>
                        <TableCell>Operation</TableCell>
                        <TableCell>Error Type</TableCell>
                        <TableCell>Error Message</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorQueueRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10}>
                            <Typography variant="body2">
                              No queued sync errors found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        errorQueueRows.map((row, index) => (
                          <TableRow key={`${row._id || "error"}-${index}`}>
                            <TableCell>
                              {formatDateTime(row.createdAt)}
                            </TableCell>
                            <TableCell>{row.status || "-"}</TableCell>
                            <TableCell>{row.repName || "Unknown"}</TableCell>
                            <TableCell>
                              {row.supervisorName || "Unassigned"}
                            </TableCell>
                            <TableCell>{row.province || "-"}</TableCell>
                            <TableCell>{row.entity || "-"}</TableCell>
                            <TableCell>{row.operation || "-"}</TableCell>
                            <TableCell>{row.errorType || "-"}</TableCell>
                            <TableCell>{row.errorMessage || "-"}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => setSelectedError(row)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {selectedError ? (
              <Card elevation={2}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                  >
                    Selected Error Detail
                  </Typography>

                  <Stack spacing={1}>
                    <Typography variant="body2">
                      <b>ID:</b> {String(selectedError?._id || "")}
                    </Typography>
                    <Typography variant="body2">
                      <b>Synchronization:</b>{" "}
                      {String(selectedError?.synchronizationId || "") || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <b>Attempt:</b>{" "}
                      {Number(selectedError?.attemptNumber || 0)}
                    </Typography>
                    <Typography variant="body2">
                      <b>Record ID:</b> {selectedError?.recordId || "-"}
                    </Typography>
                    <Typography variant="body2">
                      <b>Resolution:</b> {selectedError?.resolution || "-"}
                    </Typography>
                  </Stack>

                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 2 }}
                  >
                    recordSnapshot
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      border: "1px solid #ddd",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      overflowX: "auto",
                      maxHeight: "240px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    {JSON.stringify(
                      selectedError?.recordSnapshot || null,
                      null,
                      2,
                    )}
                  </Box>
                </CardContent>
              </Card>
            ) : null}

            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Errors By Entity
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Entity</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Unresolved</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorsByEntity.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2">
                              No error rows found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        errorsByEntity.map((row, index) => (
                          <TableRow key={`${row._id || "entity"}-${index}`}>
                            <TableCell>{row._id || "unknown"}</TableCell>
                            <TableCell align="right">
                              {Number(row.count || 0)}
                            </TableCell>
                            <TableCell align="right">
                              {Number(row.unresolved || 0)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                >
                  Errors By Type
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Error Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {errorsByType.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="body2">
                              No error rows found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        errorsByType.map((row, index) => (
                          <TableRow key={`${row._id || "type"}-${index}`}>
                            <TableCell>{row._id || "unknown"}</TableCell>
                            <TableCell align="right">
                              {Number(row.count || 0)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Stack>
        )}

        {loading ? <Typography>Loading...</Typography> : null}
      </Stack>
    </Page>
  );
};

export default SyncSupport;
